/**
 * Página admin RBAC — matriz permisos × roles editable en vivo.
 *
 * Solo accesible a admins de asociación/unión (protegido con RequirePermission
 * en App.tsx, y con guard interno por seguridad de doble-lock).
 *
 * Permite:
 *  - Ver el catálogo completo `/permissionDefinitions`.
 *  - Ver la política global de cada rol de miembro (director, subdirector, etc).
 *  - Marcar/desmarcar checkboxes para conceder o revocar permisos.
 *  - Guardar los cambios directamente en Firestore (`/rolePolicies/{role}`).
 *  - Los cambios surten efecto EN VIVO — sin redeploy — porque
 *    `useEffectivePermissions` los recarga con React Query cada 5 minutos
 *    (o al invalidar la key). Un usuario final ve el nuevo scope al refrescar.
 *
 * Aún NO cubre overrides por club (futura iteración). Ese caso se maneja hoy
 * con el script `_set-feature-flags.mjs` o edición directa en Firebase Console.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@/components/ui/Button";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/authStore";
import {
  getAllPermissionDefinitions,
  getAllPolicies,
  seedGlobalRolePolicies,
  seedPermissionDefinitions,
  setGlobalPolicyPermissions,
} from "@/lib/services/permissionPolicies";
import {
  PERMISSION_MODULES,
  PERMISSION_MODULE_LABELS,
  type PermissionModule,
} from "@/lib/constants/permissions";
import type {
  MemberRole,
  PermissionDefinition,
  RolePermissionPolicy,
} from "@/types";
import { MEMBER_ROLE_LABELS } from "@/types";

const POLICY_ROLES: MemberRole[] = [
  "director",
  "subdirector",
  "consejero",
  "instructor",
  "tesorero",
  "secretario",
];

interface DraftState {
  [role: string]: Set<string>;
}

export default function RbacAdminPage() {
  const { profile } = useAuthStore();
  const { isAdmin } = usePermissions();
  const qc = useQueryClient();

  const {
    data: perms = [],
    isLoading: loadingPerms,
    refetch: refetchPerms,
  } = useQuery<PermissionDefinition[]>({
    queryKey: ["permissionDefinitions", "all"],
    queryFn: getAllPermissionDefinitions,
  });

  const {
    data: policies = [],
    isLoading: loadingPolicies,
    refetch: refetchPolicies,
  } = useQuery<RolePermissionPolicy[]>({
    queryKey: ["rolePolicies", "all"],
    queryFn: getAllPolicies,
  });

  const [drafts, setDrafts] = useState<DraftState>({});
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const globalPolicies = useMemo(() => {
    const map = new Map<MemberRole, RolePermissionPolicy>();
    for (const p of policies) {
      if (p.scope === "global") map.set(p.role, p);
    }
    return map;
  }, [policies]);

  const byModule = useMemo(() => {
    const grouped = new Map<PermissionModule, PermissionDefinition[]>();
    for (const p of perms) {
      const mod = p.module as PermissionModule;
      if (!grouped.has(mod)) grouped.set(mod, []);
      grouped.get(mod)!.push(p);
    }
    return grouped;
  }, [perms]);

  const dirtyRoles = Object.keys(drafts);

  function currentSet(role: MemberRole): Set<string> {
    if (drafts[role]) return drafts[role];
    const p = globalPolicies.get(role);
    return new Set(p?.permissions ?? []);
  }

  function toggle(role: MemberRole, permId: string): void {
    const set = new Set(currentSet(role));
    if (set.has(permId)) set.delete(permId);
    else set.add(permId);
    setDrafts((d) => ({ ...d, [role]: set }));
  }

  async function save(): Promise<void> {
    if (!profile?.id) {
      setErrorMsg("No hay sesión");
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    setStatusMsg(null);
    try {
      for (const role of dirtyRoles) {
        const perms = [...(drafts[role] ?? [])];
        await setGlobalPolicyPermissions(role as MemberRole, perms, profile.id);
      }
      setDrafts({});
      await Promise.all([
        refetchPolicies(),
        qc.invalidateQueries({ queryKey: ["rolePolicies"] }),
      ]);
      setStatusMsg(`Guardados ${dirtyRoles.length} cambio(s).`);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function runSeed(): Promise<void> {
    if (!profile?.id) return;
    setSaving(true);
    setErrorMsg(null);
    setStatusMsg(null);
    try {
      const p = await seedPermissionDefinitions(profile.id);
      const g = await seedGlobalRolePolicies(profile.id);
      await Promise.all([refetchPerms(), refetchPolicies()]);
      setStatusMsg(
        `Semilla: permisos +${p.created}/${p.skipped} skip, políticas +${g.created}/${g.skipped} skip.`,
      );
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error en semilla");
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-gray-600">
        Solo administradores pueden acceder al panel de permisos.
      </div>
    );
  }

  if (loadingPerms || loadingPolicies) {
    return <div className="p-8 text-gray-500">Cargando permisos…</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 lg:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Permisos por rol (RBAC dinámico)
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Editá qué permisos tiene cada cargo. Los cambios aplican en toda la
            organización sin necesidad de un nuevo despliegue. Cada usuario los
            verá reflejados al recargar (o en máximo 5 minutos).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runSeed}
            disabled={saving}
          >
            Refrescar catálogo semilla
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || dirtyRoles.length === 0}
            loading={saving}
          >
            Guardar cambios ({dirtyRoles.length})
          </Button>
        </div>
      </header>

      {statusMsg && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {statusMsg}
        </div>
      )}
      {errorMsg && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {errorMsg}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-semibold">
                Permiso
              </th>
              {POLICY_ROLES.map((r) => (
                <th key={r} className="px-3 py-3 text-center font-semibold">
                  {MEMBER_ROLE_LABELS[r]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MODULES.filter((m) => byModule.has(m)).map((mod) => (
              <>
                <tr key={`h-${mod}`} className="bg-indigo-50/40">
                  <td
                    colSpan={POLICY_ROLES.length + 1}
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-indigo-700"
                  >
                    {PERMISSION_MODULE_LABELS[mod]}
                  </td>
                </tr>
                {byModule.get(mod)!.map((perm) => (
                  <tr
                    key={perm.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="sticky left-0 z-10 bg-white px-4 py-2">
                      <div className="font-medium text-gray-800">{perm.id}</div>
                      <div className="text-xs text-gray-500">
                        {perm.description}
                      </div>
                    </td>
                    {POLICY_ROLES.map((r) => {
                      const has = currentSet(r).has(perm.id);
                      const isDirty = !!drafts[r];
                      return (
                        <td key={r} className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                              isDirty ? "ring-2 ring-amber-200" : ""
                            }`}
                            checked={has}
                            onChange={() => toggle(r, perm.id)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="text-xs text-gray-500">
        Los overrides por club (política específica para un club puntual) aún no
        se editan desde esta vista. Para eso usar el script
        <code className="mx-1 rounded bg-gray-100 px-1">
          functions/_set-feature-flags.mjs
        </code>
        o Firebase Console mientras se implementa.
      </footer>
    </div>
  );
}
